import { StationView } from '@mark43/cad-api';
import { useCallback, useEffect, useRef, useState } from 'react';
import { FeatureLayer, Graphic, WebMap } from '../constructors';
import { EntityIdToObjectIdMaps } from '../helpers/entity-id-to-object-id-maps';
import { createStationFeatureLayer } from '../helpers/feature-layers';
import { createStationGraphic } from '../helpers/graphics';
import { areLatLngsEqual } from '../../location/helpers';
import type { StationViewEntity } from '../types';
import { useLayerControl, usePreviousLayer } from './use-feature-layer-control';
import { EsriMapEntityTypeEnum } from '../types';

type StationFeatureLayerProps = {
    stations: StationView[];
    selectedStationId?: number;
    selectedMapLedgerEntityType?: EsriMapEntityTypeEnum;
    webMap?: WebMap;
};

export const useStationFeatureLayer = ({
    stations,
    selectedStationId,
    selectedMapLedgerEntityType,
    webMap,
}: StationFeatureLayerProps) => {
    const allStations: StationViewEntity[] = [];
    const entityIdToObjectIdMapCacheRef = useRef<typeof EntityIdToObjectIdMaps.STATIONS>(
        EntityIdToObjectIdMaps.STATIONS
    );
    // filter out stations that don't won't show on the map
    for (let i = 0; i < stations.length; i++) {
        const station = stations[i];
        if (station.location) {
            // stations are rarely updated and the total number is limited,
            // iterate through all stations and inject entityId.
            // (this is the only layer that adds entityId in use*FeatureLayer. Other layers will provide prop entities with entityId)
            allStations.push({ ...station, entityId: station.id });
        }
    }
    const [stationsLayer, setStationsLayer] = useState<FeatureLayer>();
    const prevSelectedStationId = usePreviousLayer(selectedStationId, stationsLayer);
    const prevSelectedMapLedgerEntityType = usePreviousLayer(
        selectedMapLedgerEntityType,
        stationsLayer
    );

    // station needs to be updated:
    // if selectedMapLedgerEntityType changed from STATION or to STATION
    // if station lat/lng changed
    // if selectedStationId differs from prevSelectedStationId
    const didStationGraphicChange = useCallback(
        (currStation: StationViewEntity, prevStation: StationViewEntity) => {
            const isEntityTypeChanged =
                prevSelectedMapLedgerEntityType !== selectedMapLedgerEntityType &&
                (prevSelectedMapLedgerEntityType === EsriMapEntityTypeEnum.STATION ||
                    selectedMapLedgerEntityType === EsriMapEntityTypeEnum.STATION);

            return (
                isEntityTypeChanged ||
                !areLatLngsEqual(currStation.location, prevStation.location) ||
                (selectedStationId !== prevSelectedStationId &&
                    (currStation.id === selectedStationId ||
                        currStation.id === prevSelectedStationId))
            );
        },
        [
            prevSelectedStationId,
            selectedStationId,
            prevSelectedMapLedgerEntityType,
            selectedMapLedgerEntityType,
        ]
    );

    const createStationGraphics = useCallback(
        (stations: StationViewEntity[]) => {
            const stationGraphics: Graphic[] = [];
            for (const station of stations) {
                const objectId = entityIdToObjectIdMapCacheRef.current.get(station.entityId);
                const stationGraphic = createStationGraphic({
                    objectId,
                    stationViewEntity: station,
                    selectedStationId,
                    selectedMapLedgerEntityType,
                });
                if (stationGraphic) {
                    stationGraphics.push(stationGraphic);
                }
            }
            return stationGraphics;
        },
        [selectedStationId, selectedMapLedgerEntityType]
    );

    // handles initializing empty feature layer
    useEffect(() => {
        if (webMap) {
            const stationFeatureLayer = createStationFeatureLayer();
            setStationsLayer(stationFeatureLayer);
            webMap.layers.add(stationFeatureLayer);
        }
    }, [webMap]);

    useLayerControl<StationViewEntity>({
        layer: stationsLayer,
        currItems: allStations,
        didItemChange: didStationGraphicChange,
        createGraphics: createStationGraphics,
        entityIdToObjectIdMapCacheRef,
    });

    return { stationsLayer };
};
